import moment from "moment";

import { serializeAddonForOrderApi } from "@/lib/addon-checkout";
import { deserializeAddonValue } from "@/lib/utils";
import { Order, OrderItem, OrderCreate, OrderProgress } from "@/types/orders";
import {
	BasketAddonItem,
	Addon,
	BasketItem,
	Service,
	AddonInputType,
	Package,
	BasketAddonItemWithoutType,
} from "@/types";
import { Currency, detectUserCurrency } from "@/lib/utils/currency";
import { serviceCategoryShowsHosting } from "@/lib/service-hosting";

export function calculateOrderItemRemaining(pkg: Package, item: OrderItem, orderCreatedAt: Date) {
	const date = moment(orderCreatedAt);

	const dueDate = moment(item.createdAt).add(pkg.dueDays, "days");
	if (dueDate === undefined) return 100;

	// When progress is "In Review", pause the count by using updatedAt as the "frozen" time point
	// This ensures the percentage doesn't continue to increase while in review
	const isInReview = item.progress === OrderProgress.InReview;
	const currentTime = isInReview && item.updatedAt 
		? moment(item.updatedAt) 
		: moment();

	const isPastDue = currentTime.isAfter(dueDate);
	const timeRemaining = moment.duration(Math.abs(dueDate.diff(currentTime)));
	const totalDuration = moment.duration(Math.abs(date.diff(dueDate)));

	if (isPastDue) return 100;

	const remainingMilliseconds = timeRemaining.asMilliseconds();
	const totalMilliseconds = totalDuration.asMilliseconds();

	return ((totalMilliseconds - remainingMilliseconds) / totalMilliseconds) * 100;
}

export function calculateAddonTotal(
	addonPrice: number,
	type: AddonInputType | null,
	value: BasketAddonItem["value"],
) {
	const x = deserializeAddonValue(type, value);
	return addonPrice * (typeof x === "boolean" ? (x as unknown as number) : x);
}

export function calculateAddonsTotal(
	addons: [string, BasketAddonItemWithoutType][],
	data: Addon[] | undefined | null,
	currency?: Currency,
) {
	const userCurrency = currency ?? detectUserCurrency();
	const addonCatalog = Array.isArray(data) ? data : [];
	return addons.reduce((total, [addonId, { type, value }]) => {
		const addon = addonCatalog.find((x) => x._id === addonId);
		if (!addon) return total;
		// Get addon amount - prefer new multi-currency structure, fallback to old single amount
		const addonAmount = addon.amounts?.[userCurrency] ?? addon.amount ?? 0;
		const { inputType } = addon;

		/**
		 * Note(Curstantine):
		 * Number fields serialize empty fields as NaN, these should not be counted.
		 */
		const realValue = inputType === AddonInputType.number && Number.isNaN(value) ? 0 : value;
		return total + calculateAddonTotal(addonAmount, type ?? inputType, realValue);
	}, 0);
}

export function calculatePackageTotal(
	pkg: Package,
	addonData: Addon[] | undefined | null,
	selectedAddons: [string, BasketAddonItem][],
	currency?: Currency,
) {
	const userCurrency = currency ?? detectUserCurrency();
	const addonTotal = calculateAddonsTotal(selectedAddons, addonData, userCurrency);
	// Get package price - prefer new multi-currency structure, fallback to old single price
	const packagePrice = pkg.amounts?.[userCurrency] ?? pkg.price ?? 0;

	return {
		addonTotal: addonTotal,
		packageTotal: packagePrice,
	};
}

/** Package titled “Basic” (case-insensitive), else first tier — used for listing “Starting at” price. */
export function getBasicTierPackage(packages: Package[] | undefined): Package | undefined {
	if (!packages?.length) return undefined;
	const basic = packages.find((p) => (p.title ?? "").trim().toLowerCase() === "basic");
	return basic ?? packages[0];
}

export function hiddenPackageTitlesForService(
	service: Pick<Service, "categoryId">,
): string[] {
	const category = service.categoryId;
	if (!category || typeof category === "string") return [];
	const titles = category.hiddenPackageTitles;
	return Array.isArray(titles) ? titles : [];
}

/** Drop packages whose title is hidden on the service’s category. */
export function visibleStorefrontPackages<T extends { title?: string }>(
	packages: T[] | undefined,
	hiddenTitles: string[] | undefined,
): T[] {
	const list = packages ?? [];
	const hidden = new Set(
		(hiddenTitles ?? []).map((title) => title.trim().toLowerCase()).filter(Boolean),
	);
	if (!hidden.size) return list;
	const visible = list.filter(
		(pkg) => !hidden.has((pkg.title ?? "").trim().toLowerCase()),
	);
	return visible.length ? visible : list;
}

export function storefrontPackagesForService(service: Service): Package[] {
	return visibleStorefrontPackages(
		service.packages,
		hiddenPackageTitlesForService(service),
	);
}

/** Default selected package on the service page: Standard when present, else first tier. */
export function getDefaultSelectedPackage(packages: Package[] | undefined): Package | undefined {
	if (!packages?.length) return undefined;
	const standard = packages.find(
		(p) => (p.title ?? "").trim().toLowerCase() === "standard",
	);
	return standard ?? packages[0];
}

export function getEnvatoTemplateLicenseAmountForCurrency(service: Service, currency: Currency): number {
	const amounts = service.envatoTemplateLicenseAmounts;
	if (!amounts || typeof amounts !== "object") return 0;
	const v = amounts[currency];
	if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return 0;
	return v;
}

/**
 * Service listing / card “Starting at”: Basic-tier package + template licence (no add-ons).
 * Same components as a basket line’s package + licence row before add-ons.
 */
export function getServiceCardListingStartingAmount(service: Service, currency: Currency): number {
	const pkg = getBasicTierPackage(storefrontPackagesForService(service));
	if (!pkg) return 0;
	const packagePrice = pkg.amounts?.[currency] ?? pkg.price ?? 0;
	const pkgNumber = typeof packagePrice === "number" && Number.isFinite(packagePrice) ? packagePrice : 0;
	return pkgNumber + getEnvatoTemplateLicenseAmountForCurrency(service, currency);
}

export function calculateBasketTotal(items: BasketItem[], services: Service[], currency?: Currency) {
	const servicesKeyed: Record<string, Service> = {};
	for (const service of services) {
		servicesKeyed[service._id] = service;
		if (service.id) servicesKeyed[service.id] = service;
	}
	const userCurrency = currency ?? detectUserCurrency();

	return items.reduce((total, item) => {
		const service = servicesKeyed[item.serviceId];
		if (!service) return total;
		
		const selectedPkg = service.packages.find((x) => x._id === item.packageId);
		if (!selectedPkg) return total;
		
		const { addonTotal, packageTotal } = calculatePackageTotal(
			selectedPkg,
			service.addons,
			Object.entries(item.addons),
			userCurrency,
		);

		const amounts = service.envatoTemplateLicenseAmounts;
		let licenseLine = 0;
		if (amounts && typeof amounts === "object") {
			const v = amounts[userCurrency];
			if (typeof v === "number" && Number.isFinite(v) && v > 0) licenseLine = v;
		}

		return total + addonTotal + packageTotal + licenseLine;
	}, 0);
}

export type BasketPackageLicenseBreakdown = {
	packageTotal: number;
	licenseTotal: number;
	/** Sum of max(1, quantity) per line — for Package vs Packages wording */
	packageUnits: number;
	/** Basket lines that include a priced Envato template licence */
	licenseLineCount: number;
};

/** Per-line package + licence amounts (no add-ons). Same rules as `calculateBasketPackageAndLicenseTotal`. */
export function getBasketPackageLicenseBreakdown(
	items: BasketItem[],
	services: Service[],
	currency?: Currency,
): BasketPackageLicenseBreakdown {
	const servicesKeyed: Record<string, Service> = {};
	for (const service of services) {
		servicesKeyed[service._id] = service;
		servicesKeyed[service.id] = service;
	}
	const userCurrency = currency ?? detectUserCurrency();

	let packageTotal = 0;
	let licenseTotal = 0;
	let packageUnits = 0;
	let licenseLineCount = 0;

	for (const item of items) {
		const service = servicesKeyed[item.serviceId];
		if (!service) continue;

		const selectedPkg = service.packages.find((x) => x._id === item.packageId);
		if (!selectedPkg) continue;

		const packagePrice = selectedPkg.amounts?.[userCurrency] ?? selectedPkg.price ?? 0;
		packageTotal += packagePrice;
		packageUnits += Math.max(1, item.quantity);

		const amounts = service.envatoTemplateLicenseAmounts;
		let licenseLine = 0;
		if (amounts && typeof amounts === "object") {
			const v = amounts[userCurrency];
			if (typeof v === "number" && Number.isFinite(v) && v > 0) licenseLine = v;
		}
		licenseTotal += licenseLine;
		if (licenseLine > 0) licenseLineCount += 1;
	}

	return { packageTotal, licenseTotal, packageUnits, licenseLineCount };
}

/** Package + licence per basket line (excludes add-ons). Matches each row’s “package + licence” part of `calculateBasketTotal`. */
export function calculateBasketPackageAndLicenseTotal(
	items: BasketItem[],
	services: Service[],
	currency?: Currency,
): number {
	const { packageTotal, licenseTotal } = getBasketPackageLicenseBreakdown(items, services, currency);
	return packageTotal + licenseTotal;
}

export function calculateOrderItemsTotal(items: OrderItem[], currency?: Currency) {
	const userCurrency = currency ?? detectUserCurrency();
	return items.reduce((total, { serviceId: service, package: pkg, addons }) => {
		if (service === null) {
			throw new Error("Service data is null. This service is mostly likely deleted");
		}

		const selectedPkg = service.packages.find((x) => x._id === pkg.packageId)!;
		const addonIds: [string, BasketAddonItemWithoutType][] = addons.map((x) => [
			x.addonId,
			{ value: x.quantity },
		]);

		const addonsTotal = calculateAddonsTotal(addonIds, service.addons, userCurrency);
		// Get package price - prefer new multi-currency structure, fallback to old single price
		const packagePrice = selectedPkg.amounts?.[userCurrency] ?? selectedPkg.price ?? 0;
		return total + packagePrice * pkg.quantity + addonsTotal;
	}, 0);
}

export function transformBasketToOrderCreateItems({
	serviceId,
	addons,
	packageId,
	quantity,
	preferredDesignServiceId,
}: BasketItem): OrderCreate["items"][0] {
	const addonEntries = Object.entries(addons);
	const adx = [];

	for (let i = 0; i < addonEntries.length; i++) {
		const [addonId, { type, value }] = addonEntries[i];
		const row = serializeAddonForOrderApi(addonId, type, value);
		if (row) adx.push(row);
	}

	return {
		serviceId,
		...(preferredDesignServiceId?.trim()
			? { preferredDesignServiceId: preferredDesignServiceId.trim() }
			: {}),
		package: { packageId, quantity },
		addons: adx,
	};
}

export type SingleOrder = {
	id: any;
	summary: string;
	preferredDesignTitle?: string;
	category: any;
	name: string;
	order: string;
	orderId: string;
	date: Date;
	dueBy: string;
	status: string;
	progress: any;
	noLongerNeedsHosting: boolean;
	serviceShowsHosting: boolean;
	files: string[];
	submissions: string[];
	/** Order-level platform domain from post-checkout setup. */
	customerDomain?: string;
	domainSetupStatus?: "pending" | "submitted" | "deferred";
};

export const transformSingleOrderData = (order: Order, itemId?: string | null) => {
	const orderId = order.id;
	const orderStatus = order.orderStatus;
	const userName = `${order.userId?.firstName} ${order.userId?.lastName}`;
	const orderDate = new Date(order.createdAt);
	const orderDueBy = new Date(order.createdAt);
	const items: SingleOrder[] = order.items?.map((item: any) => {
		const itemId = item._id;
		const itemTitle = item.serviceId?.title;
		const preferredDesignTitle =
			(typeof item.preferredDesignTitle === "string" && item.preferredDesignTitle.trim()) ||
			(typeof item.preferredDesignServiceId === "object" && item.preferredDesignServiceId?.title
				? String(item.preferredDesignServiceId.title)
				: "");
		const itemCategory = item.serviceId?.categoryId.title;
		const orderProgress = item.progress;
		const serviceShowsHosting = item.serviceId
			? serviceCategoryShowsHosting(item.serviceId)
			: false;
		const packageTitle =
			item.serviceId?.packages.find((p: any) => p._id == item.package.packageId)?.title ?? "";

		return {
			id: itemId,
			summary: `${itemTitle} - ${packageTitle}${
				item.addons.length > 0 ? ", Express delivery" : ""
			}${preferredDesignTitle ? ` | Preferred design: ${preferredDesignTitle}` : ""}`,
			preferredDesignTitle,
			category: itemCategory,
			name: userName,
			order: "#" + orderId.slice(0, 4),
			orderId: orderId,
			date: orderDate,
			dueBy: item.serviceId?.dueDays
				? moment(item.createdAt).add(item.serviceId?.dueDays, "days").format("DD/MM/YYYY")
				: "-",
			status: orderStatus,
			progress: orderProgress,
			noLongerNeedsHosting: item.noLongerNeedsHosting === true,
			serviceShowsHosting,
			files: order.files,
			submissions: order.submissions,
			customerDomain: order.customerDomain?.trim() || "",
			domainSetupStatus: order.domainSetupStatus,
		};
	});

	return itemId ? (items.find((item) => item.id === itemId) ?? items[0]) : items[0];
};

export async function fetchFilesFromUrls(
	urls: string[],
	options?: { orderId: string; authToken: string },
): Promise<File[]> {
	const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com";
	const validUrls = urls.filter((url) => typeof url === "string" && url.trim() !== "");

	const filePromises = validUrls.map(async (url) => {
		const fetchUrl = options
			? `${backendUrl}/orders/${options.orderId}/files/download?fileUrl=${encodeURIComponent(url)}`
			: url;
		const response = await fetch(
			fetchUrl,
			options ? { headers: { Authorization: `Bearer ${options.authToken}` } } : undefined,
		);
		if (!response.ok) {
			throw new Error(`Failed to fetch file: ${response.status}`);
		}
		const blob = await response.blob();
		const filename = urlToFileName(url);
		return new File([blob], filename, { type: blob.type || undefined });
	});

	return Promise.all(filePromises);
}

export const removeUUIDv4Prefix = (__filename: string) => {
	if (typeof __filename !== "string") {
		return __filename;
	}

	const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-/i;

	return __filename.split("/")[__filename.split("/").length - 1].replace(uuidv4Regex, "");
};

export const extractFromS3Url = (url: string) => {
	if (!url) {
		return url;
	}
	const s3Prefix = `https://${process.env.NEXT_PUBLIC_AWS_BUCKET_NAME}.s3.${process.env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com/`;
	// Check if the URL starts with the expected S3 prefix
	if (url.startsWith(s3Prefix)) {
		return url.replace(s3Prefix, "");
	}
	// If it doesn't start with the expected S3 prefix, return the original URL.
	return url;
};

export const urlToFileName = (url: string): string => {
	return removeUUIDv4Prefix(extractFromS3Url(url));
};
